/**
 * @file SetCommunicationPreferencesForm.js
 * @description This file contains the SetCommunicationPreferencesForm component,
 * which allows users to set their communication preferences with real-time validation.
 */
import React, { useEffect, useState } from 'react';
import PhoneInput from 'react-phone-number-input/input';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import Toast from '../ui/toasts/Toast';
import { isValidPhoneNumber } from '../../utils/validate';
import { formatPhoneNumber } from '../../utils/utils';
import { getText } from '../../i18n';
import { getAnonymousEmailAddressLabel } from '../../services/userService';

const SetCommunicationPreferencesForm = ({
    onSetCommunicationPreferences,
    onboardingInfo,
    communicationPreferences = null,
}) => {
    const isSelfRegistration = onboardingInfo?.isSelfRegistration || false;

    const originalEmailAddress = isSelfRegistration
        ? onboardingInfo.profileData?.email
        : onboardingInfo?.userDetails?.email;

    const originalPhoneNumber = isSelfRegistration
        ? onboardingInfo.profileData?.mobilePhoneNumber
        : onboardingInfo?.userDetails?.mobile_phone_number;

    const initialCommPrefs =
        communicationPreferences ||
        onboardingInfo?.communicationPreferences ||
        {};

    const formattedOriginalPhone =
        originalPhoneNumber && formatPhoneNumber(originalPhoneNumber);

    const [preferences, setPreferences] = useState(() => ({
        email: initialCommPrefs.email || '',
        mobilePhoneNumber: initialCommPrefs.mobilePhoneNumber || '',
    }));
    const [errors, setErrors] = useState({});
    const [emailAddressLabel, setEmailAddressLabel] = useState('');

    const { companyCode } = onboardingInfo;

    useEffect(() => {
        let isMounted = true;

        const loadEmailAddressLabel = async () => {
            setEmailAddressLabel('');
            const label = await getAnonymousEmailAddressLabel(companyCode);

            if (isMounted && label) {
                setEmailAddressLabel(label);
            }
        };

        if (companyCode) {
            loadEmailAddressLabel();
        }

        return () => {
            isMounted = false;
        };
    }, [companyCode]);

    const emailAddressLabelText =
        emailAddressLabel || getText('msg_84add5b29527');

    const validateEmailAddress = email => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validateEmailInRealTime = email => {
        if (!email || !email.trim()) {
            return '';
        }
        const trimmed = email.trim();
        if (!validateEmailAddress(trimmed)) {
            return 'Invalid email address';
        }
        if (originalEmailAddress && trimmed === originalEmailAddress) {
            return 'Please provide a different email than the current one';
        }
        return '';
    };

    const validatePhoneInRealTime = phone => {
        if (!phone) {
            return '';
        }
        if (!isPossiblePhoneNumber(phone) || !isValidPhoneNumber(phone)) {
            return 'Invalid phone number';
        }
        try {
            if (
                formattedOriginalPhone &&
                formatPhoneNumber(phone) === formattedOriginalPhone
            ) {
                return 'Please provide a different phone number than the current one';
            }
        } catch (e) {
            // ignore formatting errors and rely on other validations
        }
        return '';
    };

    const handleChange = event => {
        const { name, value } = event.target;
        setPreferences({
            ...preferences,
            [name]: value,
        });

        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: null,
            });
        }
    };

    const handlePhoneChange = value => {
        setPreferences({
            ...preferences,
            mobilePhoneNumber: value,
        });
        if (errors.mobilePhoneNumber) {
            setErrors({
                ...errors,
                mobilePhoneNumber: null,
            });
        }
    };

    const validateForm = ({ email, mobilePhoneNumber }) => {
        const newErrors = {};
        const hasOriginalContact = Boolean(
            originalEmailAddress || formattedOriginalPhone,
        );
        const hasEnteredContact = Boolean(
            (email && email.trim()) ||
            (mobilePhoneNumber && mobilePhoneNumber.trim()),
        );

        const emailValidation = validateEmailInRealTime(email);
        if (emailValidation) {
            newErrors.email = emailValidation;
        }

        if (email && originalEmailAddress && email === originalEmailAddress) {
            newErrors.email =
                'Optional email must be different from current email';
        }

        if (mobilePhoneNumber) {
            const phoneValidation = validatePhoneInRealTime(mobilePhoneNumber);
            if (phoneValidation) {
                newErrors.mobilePhoneNumber = phoneValidation;
            }
            try {
                if (
                    formattedOriginalPhone &&
                    formatPhoneNumber(mobilePhoneNumber) ===
                        formattedOriginalPhone
                ) {
                    newErrors.mobilePhoneNumber =
                        'Optional phone must be different from current phone number';
                }
            } catch (e) {
                // ignore formatting errors
            }
        }

        if (!hasOriginalContact && !hasEnteredContact) {
            const contactRequiredMessage = getText('msg_c1911f16a7fe');
            newErrors.email = contactRequiredMessage;
            newErrors.mobilePhoneNumber = contactRequiredMessage;
        }

        return newErrors;
    };

    const handleSubmit = event => {
        event.preventDefault();
        const newErrors = validateForm(preferences);

        if (Object.keys(newErrors).length === 0) {
            onSetCommunicationPreferences(preferences);
        } else {
            setErrors(newErrors);
            const firstError = Object.values(newErrors)[0];
            Toast.error(firstError);
        }
    };

    const getLabelClassName = fieldName =>
        errors[fieldName] ? 'text-danger' : '';

    const renderPrimaryContactInfo = () => {
        return (
            <>
                {originalEmailAddress && (
                    <div className="form-group mb-3">
                        <label>{emailAddressLabelText}</label>
                        <p className="d-flex ms-4">{originalEmailAddress}</p>
                    </div>
                )}

                {formattedOriginalPhone && (
                    <div className="form-group mb-3">
                        <label>{getText('msg_77064d526523')}</label>
                        <p className="d-flex ms-4">{formattedOriginalPhone}</p>
                    </div>
                )}
            </>
        );
    };

    const getHeadingText = () => {
        return isSelfRegistration
            ? 'Contact Information'
            : 'Contact Information';
    };

    const getSubheadingText = () => {
        return isSelfRegistration
            ? 'Information provided will be used as your username and during 2-factor authentication.'
            : 'Information provided will be used as your username and during 2-factor authentication.';
    };

    return (
        <div className="form-container p-3 mt-3">
            <h2 className="iNGAGED-Typography-Heading3">{getHeadingText()}</h2>
            <small className="text-muted">{getSubheadingText()}</small>
            <form
                className="needs-validation text-left pt-3 px-2"
                noValidate
                onSubmit={handleSubmit}
            >
                {renderPrimaryContactInfo()}

                {(originalEmailAddress || formattedOriginalPhone) && (
                    <div className="text-muted small ms-1 mb-3">
                        {getText('msg_dd0c38259995')}
                    </div>
                )}

                <div className="form-group mb-3">
                    <label
                        htmlFor="email"
                        className={getLabelClassName('email')}
                    >
                        {getText('msg_84add5b29527')}
                    </label>
                    <input
                        type="email"
                        className={`form-control ${
                            errors.email ? 'is-invalid' : ''
                        }`}
                        placeholder={getText('msg_0c6c4102d4df')}
                        name="email"
                        id="email"
                        autoComplete="off"
                        value={preferences.email}
                        onChange={handleChange}
                        disabled={false}
                    />

                    {errors.email && (
                        <small className="invalid-feedback">
                            {errors.email}
                        </small>
                    )}
                </div>

                <div className="form-group mb-0">
                    <label
                        htmlFor="mobilePhoneNumber"
                        className={getLabelClassName('mobilePhoneNumber')}
                    >
                        {getText('msg_7ad09b42fec8')}
                    </label>
                    <PhoneInput
                        country="US"
                        className={`form-control ${
                            errors.mobilePhoneNumber ? 'is-invalid' : ''
                        }`}
                        onChange={handlePhoneChange}
                        name="mobilePhoneNumber"
                        id="mobilePhoneNumber"
                        placeholder={getText('msg_0c6c4102d4df')}
                        value={preferences.mobilePhoneNumber}
                        disabled={false}
                    />
                    {errors.mobilePhoneNumber && (
                        <div className="invalid-feedback">
                            {errors.mobilePhoneNumber}
                        </div>
                    )}

                    <small className="text-muted text-start">
                        {getText('msg_29614632e949')}
                    </small>
                </div>

                <div className="form-group d-grid gap-2 col-4 mx-auto text-center pt-4 pb-2">
                    <button type="submit" className="ing-button-primary-left">
                        <>
                            {getText('msg_bc981983e7f5')}
                            <i className="bi bi-chevron-right"></i>
                        </>
                    </button>

                    <div className="form-group d-grid gap-2 col-10 mx-auto text-center pt-3">
                        <small>
                            <a
                                href={`/get-support${
                                    companyCode
                                        ? `?companyCode=${companyCode}`
                                        : ''
                                }`}
                                className="pl-2"
                            >
                                {getText('msg_8a38384386fa')}
                            </a>
                        </small>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default SetCommunicationPreferencesForm;
